
import React, { useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { RootStoreContext } from '../stores/root_store';
import useAuth from '../hooks/useAuth';
import useUserIdentity from '../hooks/useUserIdentity';
import { Clock } from '../stores/clock_store';
import { PagedResult } from 'utils/types';

// type ClockType = {
//     key: string;
//     type: string;
// };


export type ClockContextType = {
    items: Clock[];
    currentClock: Clock | null;
    clockIn: (collab: string, request: string) => Promise<Clock>;
    clockOut: (clock: string) => Promise<void>;
    fetchCurrentClocks: () => Promise<Clock[]>;
    fetchClocksForCollab: (collab: string, identity: string) => Promise<Clock[]>;
    getClocksForCollab: (collab: string) => Clock[];
    fetchClocksByAssociation: (associated_type: string, associated_uuid: string, identity: string) => Promise<Clock[]>;
    getClocksByAssociation: (associated_type: string, associated_uuid: string) => Clock[];
    fetchClocksForRequest: (request: string, identity: string) => Promise<Clock[]>;
    getClocksForRequest: (request: string) => Clock[];
    fetchClock: (identity: string, clock: string) => Promise<Clock | null>,
    editViaSupport: (identity: string, clock: string, time_start: string, time_end: string, notes: string) => Promise<Clock | null>,
    resetTotals(identity: string,
        associated_type: string,
        associated_uuid: string
    ) : Promise<Clock | null>,

    deleteViaSupport(identity: string,
        clock: string,
    ) : Promise<Clock | null>,

    fetchClocksByClient(
        identity: string,
        client_identity: string,
        period_start: string,
        period_end: string,
        cursor?: string): Promise<PagedResult<Clock>>



    fetchClocksByAssistant(
        identity: string,
        assistant_identity: string,
        period_start: string,
        period_end: string,
        cursor?: string): Promise<PagedResult<Clock>>


    createManualClock(
        identity: string,
        collab: string,
        time_start: string,
        time_end: string): Promise<Clock | null>;

}

export const ClockContext = React.createContext<ClockContextType>({
    items: [] as Clock[],
    currentClock: null,
    clockOut: () => { throw "Not implemented" },
    clockIn: () => { throw "Not implemented" },
    fetchCurrentClocks: () => { throw "Not implemented" },
    fetchClocksForCollab: () => { throw "Not implemented" },
    getClocksForCollab: () => { throw "Not implemented" },
    fetchClocksByAssociation: () => { throw "Not implemented" },
    getClocksByAssociation: () => { throw "Not implemented" },
    fetchClocksForRequest: () => { throw "Not implemented" },
    getClocksForRequest: () => { throw "Not implemented" },
    fetchClock: () => { throw "Not implemented" },
    editViaSupport: () => { throw "Not implemented" },
    resetTotals: () => { throw "Not implemented" },
    deleteViaSupport: () => { throw "Not implemented" },
    fetchClocksByClient: () => { throw "Not implemented" },
    fetchClocksByAssistant: () => { throw "Not implemented" },
    createManualClock: () => { throw "Not implemented" }


});

export const ClockProvider = observer(({ children }: { children: React.ReactNode }) => {

    // const [clocks, setClocks] = useState([] as Clock[]);
	const {clockStore} = useContext(RootStoreContext);
    const { currentIdentity } = useUserIdentity();
    const { currentUser, isMe } = useAuth();
    // Identity is on its way out; the user record is enough to know
    // we're an assistant (managers included).
    const isAssistant = currentIdentity?.role == 'assistant' || !!currentUser?.is_beta;

    // A clock is ours if it belongs to our user (or, for older clocks, our identity).
    // `clock.user` arrives as the user's uuid (us-...), not the urlsafe key,
    // so compare with isMe(), which accepts either.
    const isMine = (c: Clock) => {
        if (c.user && isMe(c.user))
            return true;
        if (currentIdentity?.uuid && c.identity && c.identity == currentIdentity.uuid)
            return true;
        return false;
    };

    // The current clock is our clock that hasn't ended
    const currentClock = isAssistant ?
        (clockStore.items.find((c) => !c.time_end && isMine(c)) || null) : null;

    async function fetchCurrentClocks() {
        return clockStore.fetchCurrentClocks() as Promise<Clock[]>;
    }

    React.useEffect(() => {
        if (!isAssistant || !currentUser?.key)
            return;
        fetchCurrentClocks();
    }, [clockStore, isAssistant, currentUser?.key]);


    async function clockIn(collab: string, request: string) {
        return clockStore.clockIn(currentIdentity?.uuid || '', collab, request)
            .then((data) => {
                return data as Clock;
            });
    }

    async function clockOut(clock: string) {
        return clockStore.clockOut(clock)
            .then(() => {
                return;
            });
    }

    async function fetchClocksForCollab(collabKey: string, identity: string) {
        return clockStore.fetchClocksForCollab(collabKey, identity)
            .then((data) => {
                /// setClocks(clockStore.items);
                return data;
            });
    }

    async function fetchClocksForRequest(requestKey: string, identity: string) {
        return clockStore.fetchClocksForRequest(requestKey, identity)
            .then((data) => {
                // setClocks(clockStore.items);
                return data;
            });
    }


    async function fetchClocksByAssociation(associated_type: string, associated_uuid: string, identity: string) {
        return clockStore.fetchClocksByAssociation(associated_type, associated_uuid, identity)
            .then((data) => {
                // setClocks(clockStore.items);
                return data;
            });
    }

    const getClocksForRequest = (request: string) => {
        return clockStore.items
            .filter((m) => m.associated_uuid === request)
            .slice()
            .sort((a, b) => {
                return a.time_start > b.time_start ? -1 : 1;
            });
    };

    const getClocksForCollab = (collabKey: string) => {
        return clockStore.items
            .filter((m) => m.associated_uuid === collabKey)
            .slice()
            .sort((a, b) => {
                return a.time_start > b.time_start ? -1 : 1;
            });
    };

    const getClocksByAssociation = (associated_type: string, associated_uuid: string) => {
        return clockStore.items
            .filter((m) => m.associated_type === associated_type && m.associated_uuid === associated_uuid)
            .slice()
            .sort((a, b) => {
                return a.time_start > b.time_start ? -1 : 1;
            });
    };

    const fetchClock = async (identity: string, clock: string) => {
        return clockStore.fetchClock(identity, clock)
            .then((data) => {
                return data;
            });
    };

    const editViaSupport = (identity: string, clock: string, time_start: string, time_end: string, notes: string) => {
        return clockStore.editViaSupport(identity, clock, time_start, time_end, notes);
    };

    const resetTotals = async (identity: string,
        associated_type: string,
        associated_uuid: string
    ) => {
        return clockStore.resetTotals(identity, associated_type, associated_uuid);
    };

    const deleteViaSupport = async (identity: string,
        clock: string,
    ) => {
        return clockStore.deleteViaSupport(identity, clock);
    };

    const fetchClocksByClient = async(
        identity: string,
        client_identity: string,
        period_start: string,
        period_end: string,
        cursor = ""
    ) => {
        return clockStore.fetchClocksByClient(
            identity,
            client_identity,
            period_start,
            period_end,
            cursor
        );
    };



    const fetchClocksByAssistant = async(
        identity: string,
        assistant_identity: string,
        period_start: string,
        period_end: string,
        cursor = ""
    ) => {
        return clockStore.fetchClocksByAssistant(
            identity,
            assistant_identity,
            period_start,
            period_end,
            cursor
        );
    };
    const createManualClock = async(
        identity: string,
        collab: string,
        time_start: string,
        time_end: string,
    ) => {
        return clockStore.createManualClock(identity, collab, time_start, time_end);
    };


    const data = {
        items: clockStore.items,
        currentClock: currentClock,
        clockIn: clockIn,
        clockOut: clockOut,
        fetchCurrentClocks,
        fetchClocksForCollab: fetchClocksForCollab,
        getClocksForCollab: getClocksForCollab,
        fetchClocksByAssociation: fetchClocksByAssociation,
        getClocksByAssociation: getClocksByAssociation,
        fetchClocksForRequest: fetchClocksForRequest,
        getClocksForRequest: getClocksForRequest,
        fetchClock: fetchClock,
        editViaSupport: editViaSupport ,
        resetTotals,
        fetchClocksByClient,
        fetchClocksByAssistant,
        createManualClock,
        deleteViaSupport
    };

    return (
        <ClockContext.Provider value={data}>{ children }</ClockContext.Provider>
    );

});
